import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Breadcrumb from '../../../components/Breadcrumb';
import Page from '../../../components/Page';
import client from '../../../lib/feathers';

/**
 * /admin/library-imports
 *
 * Cross-user index of every CellarTracker import that's ever been uploaded.
 * Lets admin see import activity at a glance without first navigating into
 * a specific user. Click any row to jump to the review page.
 *
 * User column is hydrated client-side via a bulk admin/user lookup for the
 * unique userIds in the import list — keeps the imports endpoint cheap and
 * avoids forcing a join server-side.
 */

const STATUS_PILLS = {
  uploaded: 'bg-gray-100 text-gray-800',
  previewed: 'bg-amber-100 text-amber-800',
  complete: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

export default function LibraryImportsIndex() {
  const navigate = useNavigate();
  const [imports, setImports] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await client.service('admin/library-import').find({
          query: { $limit: 100 },
        });
        const rows = res.data || [];
        setImports(rows);

        const ids = [...new Set(rows.flatMap((r) => [r.userId, r.uploadedById]).filter(Boolean))];
        if (ids.length > 0) {
          const userRes = await client.service('admin/user').find({
            query: { id: { $in: ids }, $limit: ids.length },
          });
          const lookup = {};
          (userRes.data || []).forEach((u) => { lookup[u.id] = u; });
          setUsersById(lookup);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <Page title="Library Imports">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <Breadcrumb elements={[{ name: 'Library Imports' }]} />
      </div>

      {error && <div className="py-4 text-sm text-red-700">{error}</div>}
      {loading && <div className="py-4 text-sm text-gray-500">Loading…</div>}

      {!loading && imports.length === 0 && (
        <div className="py-8 text-sm text-gray-500">
          No imports yet. Open a user&apos;s detail page and use <strong>Import Library</strong> to start one.
        </div>
      )}

      {!loading && imports.length > 0 && (
        <div className="py-4">
          <table className="w-full text-sm">
            <thead className="text-left text-gray-500 text-xs uppercase">
              <tr>
                <th className="py-2 pr-3">When</th>
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Uploaded by</th>
                <th className="py-2 pr-3">Filename</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3 text-right">Rows</th>
                <th className="py-2 pr-3 text-right">Ready</th>
                <th className="py-2 pr-3 text-right">Created</th>
                <th className="py-2 pr-3 text-right">Errored</th>
              </tr>
            </thead>
            <tbody>
              {imports.map((imp) => {
                const owner = usersById[imp.userId];
                const uploader = usersById[imp.uploadedById];
                return (
                  <tr
                    key={imp.id}
                    className="border-t border-gray-200 hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/admin/library-imports/${imp.id}`)}
                  >
                    <td className="py-2 pr-3">{new Date(imp.createdAt).toLocaleString()}</td>
                    <td className="py-2 pr-3">
                      {owner ? (
                        <Link
                          to={`/admin/users/${owner.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-red-600 hover:text-red-800"
                        >
                          {owner.firstName} {owner.lastName}
                        </Link>
                      ) : (
                        <span className="text-gray-400">{imp.userId.slice(0, 8)}…</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      {uploader ? `${uploader.firstName} ${uploader.lastName}` : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-2 pr-3">{imp.filename || '—'}</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${STATUS_PILLS[imp.status] || 'bg-gray-100 text-gray-700'}`}>
                        {imp.status}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right">{imp.summary?.rowCount ?? '—'}</td>
                    <td className="py-2 pr-3 text-right">{imp.summary?.ready ?? '—'}</td>
                    <td className="py-2 pr-3 text-right">{imp.summary?.createdEntries ?? 0}</td>
                    <td className="py-2 pr-3 text-right">
                      {imp.summary?.errored > 0 ? (
                        <span className="text-red-700">{imp.summary.errored}</span>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Page>
  );
}
